import { loginUser } from '@/api/login';
import { LoginUserResType } from '@/api/types';
import { BAD_RESPONSE, LOGIN_RESULTS } from '@/app/shared/constants';
import { publishReceiveLoginResult } from '@/app/shared/publishEventHelpers';
import { BadResponse, WhiteLabelLoginParams } from '@/app/types/types';
import { fetchApplicationVersion } from '@/applicationVersionService';
import { logErrorInfo, updateRollbarConfig } from '@/RollbarErrorTracking';
import { toast, ToastVariant } from '@nyshex/keel';

const getRedirect = (isAdmin?: boolean) =>
  isAdmin
    ? process.env.NEXT_PUBLIC_ADMIN_DOMAIN
    : process.env.NEXT_PUBLIC_MEMBERS_DOMAIN;

const isLoopback = (hostname: string) =>
  hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';

// Only ever redirect a post-login user to an https NYSHEX-owned origin. redirect_to arrives from
// the URL, so an unvalidated value turns any NYSHEX-branded login page into an open redirect.
export const isAllowedRedirect = (value: string): boolean => {
  try {
    const url = new URL(value);
    if (
      url.protocol === 'https:' &&
      (url.hostname === 'nyshex.com' || url.hostname.endsWith('.nyshex.com'))
    ) {
      return true;
    }
    // Local development returns to a loopback app over http. Allowed only when this page is
    // itself served from loopback, so a deployed environment can never be talked into it. The
    // scheme check must not be skipped here: URL parses an authority for any scheme followed by
    // "//", so javascript://localhost/%0a... has a loopback hostname and replace() would run it.
    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      typeof window !== 'undefined' &&
      isLoopback(window.location.hostname) &&
      isLoopback(url.hostname)
    );
  } catch {
    return false;
  }
};

// Picks where a user goes after login: the requested redirect_to when it is allowed, otherwise
// the fallback. `refused` separates a rejected redirect_to from one that was never supplied, so
// callers can flag it on their analytics event. The refusal is logged without the value because
// it is attacker-supplied.
export const resolveRedirect = <Fallback extends string | undefined>(
  requested: string | null,
  fallback: Fallback
): { target: string | Fallback; refused: boolean } => {
  if (!requested) return { target: fallback, refused: false };
  if (isAllowedRedirect(requested))
    return { target: requested, refused: false };

  logErrorInfo({
    message: 'Refused a redirect_to target outside NYSHEX origins',
  });
  return { target: fallback, refused: true };
};

export const loginToApp = async (
  loginData: {
    username: string;
    password: string;
    acceptTos?: boolean;
  },
  redirect: string | null,
  whiteLabelParam?: WhiteLabelLoginParams
) => {
  const payload = {
    userName: loginData.username,
    password: loginData.password,
    ...(loginData.acceptTos && { acceptTos: true }),
    ...(whiteLabelParam && { whiteLabelBusiness: whiteLabelParam }),
  };
  const response = await loginUser(payload);

  if ('consentRequired' in response) {
    publishReceiveLoginResult({
      resultType: 'TOS_CONSENT_REQUIRED',
      whiteLabel: whiteLabelParam && whiteLabelParam,
    });
    return LOGIN_RESULTS.NEED_CONSENT;
  }

  if ('ok' in response && !(response as BadResponse).ok) return BAD_RESPONSE;

  const user = response as LoginUserResType;
  localStorage.setItem('userId', user.userId);

  const { target: redirectValue, refused: redirectRefused } = resolveRedirect(
    redirect,
    getRedirect(user.properties.isAdmin)
  );

  publishReceiveLoginResult(
    {
      resultType: 'SUCCESS',
      redirectLink: redirectValue,
      whiteLabel: whiteLabelParam && whiteLabelParam,
      redirectRefused,
    },
    user.userId
  );

  return redirectValue;
};

export const toastTryAgain = (content: string) => {
  toast({
    variant: ToastVariant.Danger,
    content,
    dismissable: true,
  });
};

export const updateRollbar = async () => {
  const userId = localStorage.getItem('userId');
  const version = await fetchApplicationVersion();

  updateRollbarConfig(`login-ui-${version}`, userId);
};

export const getDefaultRedirect = () => {
  if (typeof window === 'undefined') return '';
  const environment = window.location.origin.split('.')[1];
  return environment === 'nyshex'
    ? 'https://members.nyshex.com'
    : `https://members.${environment}.nyshex.com`;
};
